/**
 * Guard against silent re-authentication loops.
 *
 * /login redirects to the IdP on mount with no user interaction. When the IdP
 * still holds a live SSO session and the client is trusted, it hands back an
 * authorization code immediately — no password prompt, no consent screen. That
 * is the intended SSO experience, and it is also what turns any *persistent*
 * downstream failure into an unbreakable loop:
 *
 *   protected page -> 401 -> refresh (succeeds) -> 401 -> session-expired
 *     -> /login -> IdP issues a code silently -> new tokens -> 401 -> ...
 *
 * The user sees the page flicker forever and is never asked for a password, so
 * there is nothing they can do about it. The known trigger was the backend
 * caching a stale JWKS key (fixed separately), but any bug that makes a freshly
 * minted token unusable produces the same shape.
 *
 * So: count how often we bounce through /login in a short window, and once that
 * looks like a loop, stop redirecting and show the user something actionable.
 */

const STORAGE_KEY = "auth_login_attempts";
const WINDOW_MS = 60_000;
const MAX_ATTEMPTS = 3;

type AttemptRecord = { count: number; firstAt: number };

const read = (): AttemptRecord | null => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as AttemptRecord;
        if (typeof parsed?.count !== "number" || typeof parsed?.firstAt !== "number") {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

const write = (record: AttemptRecord | null): void => {
    try {
        if (record) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
        } else {
            sessionStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // storage unavailable (private mode, quota): fail open rather than
        // blocking login entirely
    }
};

/**
 * Record an automatic redirect to the IdP.
 * @returns true when this attempt is allowed to proceed, false when we have
 *          bounced too many times and should stop and tell the user.
 */
export function recordLoginAttempt(): boolean {
    const now = Date.now();
    const existing = read();

    if (!existing || now - existing.firstAt > WINDOW_MS) {
        write({ count: 1, firstAt: now });
        return true;
    }

    const next = { count: existing.count + 1, firstAt: existing.firstAt };
    write(next);
    return next.count <= MAX_ATTEMPTS;
}

/** True when we have already given up on automatic re-authentication. */
export function isLoginLoopDetected(): boolean {
    const record = read();
    if (!record) return false;
    if (Date.now() - record.firstAt > WINDOW_MS) {
        write(null);
        return false;
    }
    return record.count > MAX_ATTEMPTS;
}

/** Called after a successful sign-in so the next failure starts from zero. */
export function clearLoginAttempts(): void {
    write(null);
}
